import { getNode, getNodes, trigger } from '@gravityforms/utils';
import elements from './elements';
import { setProgressBar } from './progress-bar';
import state from './state';
import { showValidationError, hideValidationError, validate } from './validation';

/**
 * @function enablePrev
 * @description Enables the previous button.
 *
 * @since 1.0.0
 *
 */
const enablePrev = () => {
	elements.buttons.prev.removeAttribute( 'disabled' );
};

/**
 * @function disablePrev
 * @description Disables the previous button.
 *
 * @since 1.0.0
 *
 */
const disablePrev = () => {
	elements.buttons.prev.setAttribute( 'disabled', true );
};

/**
 * @function showNext
 * @description Shows the next button.
 *
 * @since 1.0.0
 *
 */
const showNext = () => {
	getNode(
		`.gform-conversational__nav-button--next-${ state.activeIds[ state.activeFieldIndex ] }`,
		document,
		true,
	).classList.add( 'active' );
};

/**
 * @function hideNext
 * @description Hides the next button.
 *
 * @since 1.0.0
 *
 */
const hideNext = () => {
	getNode(
		`.gform-conversational__nav-button--next-${ state.activeIds[ state.activeFieldIndex ] }`,
		document,
		true,
	).classList.remove( 'active' );
};

/**
 * @function enableNext
 * @description Enables the next button.
 *
 * @since 1.0.0
 *
 */
const enableNext = () => {
	elements.buttons.next.removeAttribute( 'disabled' );
};

/**
 * @function disableNext
 * @description Disables the previous button.
 *
 * @since 1.0.0
 *
 */
const disableNext = () => {
	elements.buttons.next.setAttribute( 'disabled', true );
};

/**
 * @function showSubmit
 * @description Shows the submit button.
 *
 * @since 1.0.0
 *
 */
const showSubmit = () => {
	getNode(
		`.gform-conversational__nav-button--submit-${ state.activeIds[ state.activeFieldIndex ] }`,
		document,
		true,
	).classList.add( 'active' );
};

/**
 * @function hideSubmit
 * @description Hides the submit button.
 *
 * @since 1.0.0
 *
 */
const hideSubmit = () => {
	getNode(
		`.gform-conversational__nav-button--submit-${ state.activeIds[ state.activeFieldIndex ] }`,
		document,
		true,
	).classList.remove( 'active' );
};

/**
 * @function handleNavButtonDisplay
 * @description Handles the display of the Previous, Next and Submit buttons as well as the progress bar.
 *
 * @since 1.0.0
 *
 */
export const handleNavButtonDisplay = () => {
	if ( 1 === state.activeIds.length ) {
		// Only one step.
		disablePrev();
		hideNext();
		disableNext();
		showSubmit();
	} else if ( state.progress === 1 ) {
		// First step.
		disablePrev();
		showNext();
		enableNext();
		hideSubmit();
	} else if ( state.progress === state.activeIds.length ) {
		// Last step.
		hideNext();
		disableNext();
		enablePrev();
		showSubmit();
	} else {
		// Middle step.
		enablePrev();
		showNext();
		enableNext();
		hideSubmit();
	}

	if ( elements.progressBar ) {
		setProgressBar( state.progress, state.activeIds.length );
	}
};

/**
 * @description Gets the field ID associated with the current field.
 *
 * @since 1.0.0
 *
 * @return {number} Returns the ID of the current field.
 */
const getFieldId = () => {
	const idParts = elements.fields[ state.activeFieldIndex ].id.split( '_' );
	return idParts.length < 3 ? 0 : idParts[ 2 ];
};

/**
 * @function navigate
 * @description Navigates to next or previous field depending on direction provided.
 *
 * @since 1.0.0
 *
 * @param {string} direction Direction to navigate, one of `next` or `previous`.
 */
const navigate = ( direction ) => {
	// If direction is not next or previous, return early.
	if ( ! [ 'next', 'previous' ].includes( direction ) ) {
		return;
	}

	elements.fields[ state.activeFieldIndex ].classList.remove( 'active-step' );

	if ( direction === 'next' ) {
		state.activeFieldIndex++;
		state.progress++;

		// Skip fields that are hidden by conditional logic.
		while (
			state.activeFieldIndex < elements.fields.length &&
			elements.fields[ state.activeFieldIndex ].style.display === 'none'
		) {
			// Don't let active field index go above field count.
			if ( state.activeFieldIndex >= elements.fields.length - 1 ) {
				break;
			}

			state.activeFieldIndex++;
		}
	} else {
		state.activeFieldIndex--;
		state.progress--;

		// Skip fields that are hidden by conditional logic.
		while (
			state.activeFieldIndex <= 0 &&
			elements.fields[ state.activeFieldIndex ].style.display === 'none'
		) {
			// Don't let active field index go below 0.
			if ( state.activeFieldIndex === 0 ) {
				break;
			}

			state.activeFieldIndex--;
		}
	}

	handleNavButtonDisplay();

	elements.fields[ state.activeFieldIndex ].classList.add( 'active-step' );

	const inputNodes = getInputNodes();

	inputNodes[ 0 ].focus();
};

/**
 * @function handleNextClick
 * @description Handler for the click event on the next button.
 *
 * @since 1.0.0
 *
 * @param {Event} event The event object.
 */
const handleNextClick = ( event ) => {
	if ( state.progress === state.activeIds.length ) {
		return;
	}

	trigger( {
		event: 'gfcf/conversational/navigate/start',
		native: false,
		data: { target: elements.fields[ state.activeFieldIndex ] },
	} );

	const fieldId = getFieldId();

	validate( fieldId ).then( ( errorMessage ) => {
		hideValidationError();

		if ( errorMessage ) {
			trigger( {
				event: 'gfcf/conversational/navigate/error',
				native: false,
				data: { fieldId, event, target: elements.fields[ state.activeFieldIndex ] },
			} );
			const formId = getNode( 'input[name="gform_submit"]', document, true ).value;
			showValidationError( formId, fieldId, errorMessage );
			return;
		}

		navigate( 'next' );

		trigger( {
			event: 'gfcf/conversational/navigate/next',
			native: false,
			data: { target: elements.fields[ state.activeFieldIndex ] },
		} );
	} );
};

/**
 * @function handlePrevClick
 * @description Handler for the click event on the previous button.
 *
 * @since 1.0.0
 *
 */
const handlePrevClick = () => {
	if ( state.activeFieldIndex === 0 ) {
		return;
	}

	trigger( {
		event: 'gfcf/conversational/navigate/start',
		native: false,
		data: { target: elements.fields[ state.activeFieldIndex ] },
	} );

	hideValidationError();
	navigate( 'previous' );

	trigger( {
		event: 'gfcf/conversational/navigate/prev',
		native: false,
		data: { target: elements.fields[ state.activeFieldIndex ] },
	} );
};

/**
 * @function handleSubmitClick
 * @description Handler for the click event on the submit button.
 *
 * @since 1.0.0
 *
 */
const handleSubmitClick = () => {
	const fieldId = getFieldId();

	validate( fieldId ).then( ( errorMessage ) => {
		hideValidationError();

		if ( errorMessage ) {
			const formId = getNode( 'input[name="gform_submit"]', document, true ).value;
			showValidationError( formId, fieldId, errorMessage );
			return;
		}

		elements.form.submit();
	} );
};

/**
 * @function setupForm
 * @description Sets up the form.
 *
 * @since 1.0.0
 *
 */
const setupForm = () => {
	if ( state.formSubmitted ) {
		return;
	}

	handleNavButtonDisplay();

	elements.fields[ state.activeFieldIndex ].classList.add( 'active-step' );
};

/**
 * @function bindEvents
 * @description Adds all the events listeners.
 *
 * @since 1.0.0
 *
 */
const bindEvents = () => {
	elements.buttons.next.addEventListener( 'click', handleNextClick );
	elements.buttons.prev.addEventListener( 'click', handlePrevClick );
	elements.buttons.allNextButtons.forEach( ( node ) => {
		node.addEventListener( 'click', handleNextClick );
	} );
	elements.buttons.allSubmitButtons.forEach( ( node ) => {
		node.addEventListener( 'click', handleSubmitClick );
	} );
};

/**
 * @function setupElements
 * @description Load DOM elements into variables.
 *
 * @since 1.0.0
 *
 */
const setupElements = () => {
	// If form is submitted, return early.
	if ( state.formSubmitted ) {
		return;
	}

	// Remove old elements from the DOM.
	elements.inputs.pageTarget.remove();
	elements.inputs.pageSource.remove();
};

/**
 * @function setupState
 * @description Set up the state.
 *
 * @since 1.0.0
 *
 */
const setupState = () => {
	elements.fields.forEach( ( field ) => {
		state.activeIds.push( field.id );
	} );
};

/**
 * @function bindKeyboardNavigation
 * @description Set up keyboard navigation.
 *
 * @since 1.0.0
 *
 */
const bindKeyboardNavigation = () => {
	// If form is submitted, don't bind keyboard nav as elements won't be present.
	if ( state.formSubmitted ) {
		return;
	}

	elements.form.addEventListener( 'keydown', function( event ) {
		if ( event.keyCode === 13 ) {
			event.preventDefault();
			if ( 1 === state.activeIds.length || state.progress === state.activeIds.length ) {
				handleSubmitClick();
			} else {
				handleNextClick();
			}
		}

		const activeEl = document.activeElement;
		const inputNodes = getInputNodes();
		if ( activeEl === inputNodes[ 0 ] ) {
			const isTabPressed = ( event.key === 'Tab' || event.keyCode === 9 );
			if ( ! isTabPressed ) {
				return;
			}
			if ( event.shiftKey && state.activeFieldIndex !== 0 ) {
				event.preventDefault();
				handlePrevClick();
			}
		}
	} );

	elements.buttons.allNextButtons.forEach( ( node ) => {
		node.addEventListener( 'keydown', function( e ) {
			const isTabPressed = ( e.key === 'Tab' || e.keyCode === 9 );

			if ( ! isTabPressed ) {
				return;
			}
			if ( ! e.shiftKey ) {
				e.preventDefault();
				handleNextClick();
			}
		} );
	} );
};

/**
 * @function getInputNodes
 * @description Get Input Nodes.
 *
 * @since 1.0.0
 *
 */
const getInputNodes = () => {
	const activeFieldContainer = getNode( '.active-step', document, true );
	return getNodes( 'textarea:not([disabled]), input:not([disabled]), select:not([disabled])', true, activeFieldContainer, true );
};

/**
 * @function init
 * @description Initializes conversational forms navigation.
 *
 * @since 1.0.0
 *
 */
const init = () => {
	state.formSubmitted = getNode( 'gform-conversational' ).dataset.formSubmitted === 'true';
	setupState();
	setupElements();
	bindEvents();
	bindKeyboardNavigation();
	setupForm();
	// Fix case where first field is conditionally hidden.
};

export default init;
